import { logger } from "@scott/logger";
import type { GeometryTableProps } from "@/scott/ui/blocks/geometry-table/geometry-table";
import { GeometryTable } from "@/scott/ui/blocks/geometry-table/geometry-table";
import { getGeometryParameters } from "@/services/product/get-geometry-parameters";
import type { UiConfiguration } from "@/types/page-block";

export interface GeometryDataBlockProps extends UiConfiguration {
  sys?: { id: string };
  internalName: string;
  title?: string;
  productCodes?: string[];
  anchorId?: string;
  language?: string;
  columnWidth?: GeometryTableProps["columnWidth"];
  showUnitToggle?: boolean;
  defaultUnit?: GeometryTableProps["defaultUnit"];
  microcopies?: GeometryTableProps["microcopies"];
  mode: GeometryTableProps["mode"];
}

const DEFAULT_MICROCOPIES: GeometryTableProps["microcopies"] = {
  frameSize: "Frame size",
  unitMm: "mm",
  unitInches: "inches",
  sizesIn: "Sizes in:",
};

export type GeometryTableData = {
  tableProps: GeometryTableProps;
};

export async function fetchGeometryTableData(
  props: Readonly<GeometryDataBlockProps>
): Promise<GeometryTableData | null> {
  const normalizedProductCodes =
    props.productCodes?.map((code) => code.toString().trim()) ?? [];

  if (normalizedProductCodes.length === 0 || !props.language) {
    return null;
  }

  const geometryParams = await getGeometryParameters(
    normalizedProductCodes,
    props.language
  );

  if (!geometryParams.measurements.length) {
    logger.warn("GeometryDataBlock: no geometry data found", {
      internalName: props.internalName,
    });
    return null;
  }

  return {
    tableProps: {
      anchorId: props.anchorId,
      backgroundColor: props.backgroundColor,
      columnWidth: props.columnWidth,
      defaultUnit: props.defaultUnit ?? "mm",
      imageUrl: geometryParams.imageUrl,
      measurements: geometryParams.measurements,
      microcopies: props.microcopies ?? DEFAULT_MICROCOPIES,
      mode: props.mode,
      paddingBottom: props.paddingBottom,
      paddingTop: props.paddingTop,
      showUnitToggle: props.showUnitToggle ?? true,
      sizes: geometryParams.sizes,
    },
  };
}

export async function GeometryDataBlock(
  props: Readonly<GeometryDataBlockProps>
) {
  const data = await fetchGeometryTableData(props);
  if (!data) {
    return null;
  }
  return <GeometryTable {...data.tableProps} />;
}
